/* eslint-disable max-len, jsdoc/no-undefined-types */
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback } from 'react';
import isEqualWith from 'lodash/isEqualWith.js';
import throttle from 'lodash/throttle.js';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import useStableMemo from '@zenbusiness/application-commons-hooks/useStableMemo';
import useGettableState from '@zenbusiness/application-commons-hooks/useGettableState';
import useAsyncEffect from '@zenbusiness/application-commons-hooks/useAsyncEffect';
import memoize from '@zenbusiness/application-commons-utils/memoize';
import AuthenticationClient from "./client.mjs";
import AuthStorage from "./auth-storage.mjs";
import { useAuthentication, AuthenticationContext } from "./context.mjs";
/** @typedef {import('./auth-storage').AuthenticationState} AuthenticationState */
/** @typedef {import('./context').AuthenticationContextObject} AuthenticationContextObject */
/**
 * @typedef AuthenticationProviderProps
 * @property {object} [auth0config] Auth0 configuration data. DEPRECATED
 * @property {string} clientID
 * @property {string} domain
 * @property {string} audience
 * @property {string} [connection]
 * @property {string} [responseType]
 * @property {string} [scope]
 * @property {string} [loginUri] URI path of the login page
 * @property {string} [logoutUri] URI path of the logout page
 * @property {string} [callbackUri] URI path of the callback page.
 * @property {string} [redirectUri] Full url auth0 should return to after login
 * @property {(auth: AuthenticationState) => void} [onCredentialsChange] Callback invoked when the access token changes
 * @property {(auth: AuthenticationState) => void} [onIdentify] Callback invoked when the current user is identified
 * (at page load or login)
 * @property {() => void} [onUnidentify] Callback invoked at logout
 * @property {boolean} [isWebView]
 * @property {import('react').ReactNode | ((context: AuthenticationContextObject) => import('react').ReactNode)} children
 */
/**
 * Authentication context provider and state management component
 *
 * @param {AuthenticationProviderProps} props
 * @returns {import('react').ReactComponentElement<any,any> | import('react/jsx-dev-runtime').JSX.Element}
 * @component
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function AuthenticationProviderInternal({
  auth0config = {},
  clientID = auth0config.clientID,
  domain = auth0config.domain,
  audience = auth0config.audience,
  connection = auth0config.connection,
  responseType = 'token id_token',
  scope = 'openid profile email',
  loginUri = '/d/login',
  logoutUri = '/d/logout',
  callbackUri = '/d/callback',
  redirectUri = `${document.location.origin}${callbackUri}`,
  onCredentialsChange,
  onIdentify,
  onUnidentify,
  isWebView,
  children
}) {
  if (!clientID || !domain || !audience) {
    throw new Error('AuthenticationProvider is missing auth0config');
  }
  const {
    pathname
  } = window.location;

  /** @type {AuthenticationClient} */
  const webAuth = useStableMemo(() => new AuthenticationClient({
    clientID,
    domain,
    audience,
    connection,
    responseType,
    scope,
    loginUri,
    logoutUri,
    callbackUri,
    redirectUri
  }), [clientID, domain, audience, connection]);

  // Process idtoken payload from WebView idToken
  useMemo(() => {
    if (isWebView) {
      AuthStorage.initializeWebview();
    }
  }, [isWebView]);
  const [loading, setLoading, getLoading] = useGettableState(true);
  const [authentication, setAuthentication, getAuthentication] = useGettableState(AuthStorage.toJSON());
  const [exception, setException] = useState(null);
  if (exception) throw exception;
  const {
    idToken,
    authenticated,
    username
  } = authentication;
  const extend = useCallback(async () => {
    // if not logged in, nothing to extend
    if (!idToken) return;
    let auth;
    try {
      auth = await webAuth.extendSession();
    } catch (err) {
      setException(err);
      auth = /** @type {AuthenticationState} */{
        authenticated: false,
        user: {}
      };
    }
    if (isEqualWith(auth, getAuthentication())) {
      // auth didn't change, we can stop here
      return;
    }
    setAuthentication(auth);
    if (onCredentialsChange) {
      onCredentialsChange(auth);
    }
  }, [idToken, getAuthentication, setAuthentication, onCredentialsChange, webAuth]);

  // this effect will trigger a session extend on mount and if the page url changes.
  useAsyncEffect(async () => {
    if (!isWebView) {
      await extend();
    }
    if (getLoading()) setLoading(false);
  }, [pathname]);
  useAsyncEffect(async () => {
    // If the identity changes
    if (authenticated && username && onIdentify) {
      onIdentify(authentication);
    }
  }, [authenticated, username, onIdentify]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const login = useCallback(throttle(redirectDestination => webAuth.login(redirectDestination), 1000), [webAuth]);
  const logout = useMemo(() => throttle(redirectTo => {
    setTimeout(() => {
      webAuth.logout({
        redirectTo
      });
    });
    setAuthentication({
      authenticated: false,
      user: null
    });
    if (onUnidentify) onUnidentify();
    if (onCredentialsChange) {
      onCredentialsChange({
        authenticated: false,
        user: {}
      });
    }
  }, 1000), [webAuth, setAuthentication, onCredentialsChange, onUnidentify]);
  const callback = useMemo(() => memoize(async () => {
    const auth = await webAuth.callback();
    setAuthentication(auth);
    if (onCredentialsChange) {
      onCredentialsChange(auth);
    }
    if (onIdentify) {
      onIdentify(auth);
    }
    return auth;
  }, {
    maxAge: 1000
  }), [webAuth, setAuthentication, onCredentialsChange, onIdentify]);
  const resetPassword = useCallback((...args) => webAuth.resetPassword(...args), [webAuth]);
  const context = /** @type {AuthenticationContextObject} */useMemoObject({
    ...authentication,
    /* ^ this provides the following:
     * type
     * idToken
     * accessToken
     * username
     * expiresAt
     * accountUuid
     * authenticated
     * obo
     * user
    */
    user: authentication.user || {},
    login,
    logout,
    callback,
    resetPassword,
    authentication,
    setAuthentication
  });

  // if we receive a function, execute it and pass in the client
  if (typeof children === 'function') {
    // eslint-disable-next-line no-param-reassign
    children = loading ? null : /** @type {import('react').ReactNode} */children(context);
  }
  return /*#__PURE__*/_jsx(AuthenticationContext.Provider, {
    value: context,
    children: loading ? null : children
  });
}
AuthenticationProviderInternal.propTypes = {
  auth0config: PropTypes.shape({
    clientID: PropTypes.string,
    domain: PropTypes.string,
    audience: PropTypes.string,
    connection: PropTypes.string
  }),
  clientID: PropTypes.string,
  domain: PropTypes.string,
  audience: PropTypes.string,
  connection: PropTypes.string,
  responseType: PropTypes.string,
  scope: PropTypes.string,
  loginUri: PropTypes.string,
  logoutUri: PropTypes.string,
  callbackUri: PropTypes.string,
  redirectUri: PropTypes.string,
  onCredentialsChange: PropTypes.func,
  onIdentify: PropTypes.func,
  onUnidentify: PropTypes.func,
  isWebView: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
};

/**
 * Authentication context provider and state management component
 *
 * @param {AuthenticationProviderProps} props
 * @returns {import('react').ReactComponentElement<any,any> | import('react/jsx-dev-runtime').JSX.Element}
 * @component
 */
export function AuthenticationProvider(props) {
  // Because we shouldn't conditionally use hooks, this wrapper component exists to
  // check if the authentication context already exists on the page (such as in a
  // federated application). In that scenario, we do not need to re-initialize auth.

  const parentContext = useAuthentication();
  if (parentContext) {
    const {
      children
    } = props;
    // if we receive a function, execute it and pass in the client
    if (typeof children === 'function') {
      return (/** @type {import('react/jsx-dev-runtime').JSX.Element} */children(parentContext)
      );
    }
    return (/** @type {import('react/jsx-dev-runtime').JSX.Element} */children
    );
  }
  return /*#__PURE__*/_jsx(AuthenticationProviderInternal, {
    ...props
  });
}
AuthenticationProvider.propTypes = AuthenticationProviderInternal.propTypes;